import React from "react";
import { Wordmark, FONT_FAMILIES } from "../landing/Wordmark";
import { ImageUploader } from "./ImageUploader";
import { Field, ArrayEditor, REC } from "./AdminPrimitives";

// ──────────────────────────────────────────────────────────────────────────────
// Each tab is a small, focused component receiving (content, update) from the
// orchestrating AdminDashboard. Test-IDs are preserved verbatim from the legacy
// monolithic file so existing tests continue to pass.
// ──────────────────────────────────────────────────────────────────────────────

export const BrandNavTab = ({ content, update }) => (
    <div>
        <div className="mb-6 p-6 rounded-2xl border border-oth-line/70 bg-white" data-testid="brand-preview">
            <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Live preview</div>
            <Wordmark
                logoUrl={content.brand?.logo_url}
                brandName={content.brand?.name}
                wordmark={content.brand?.wordmark}
                logoSize={content.brand?.logo_size}
                nameColor={content.brand?.name_color}
                nameFont={content.brand?.name_font}
                nameSize={content.brand?.name_size}
            />
        </div>

        <Field label="Brand Name" value={content.brand?.name} onChange={(v) => update("brand.name", v)} testId="brand-name" />
        <Field label="Wordmark" value={content.brand?.wordmark} onChange={(v) => update("brand.wordmark", v)} testId="brand-wordmark" />

        <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">Brand name font</div>
            <select
                value={content.brand?.name_font || ""}
                onChange={(e) => update("brand.name_font", e.target.value)}
                className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-3 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
                data-testid="brand-name-font"
            >
                <option value="">Default (General Sans)</option>
                {Object.keys(FONT_FAMILIES).map((f) => (
                    <option key={f} value={f} style={{ fontFamily: FONT_FAMILIES[f] }}>{f}</option>
                ))}
            </select>
            <div className="text-[11px] text-oth-ink3 mt-1.5 font-body">
                Choose any of ten editorial / premium typefaces. Empty = our default General Sans.
            </div>
        </div>

        <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">Brand name size (px)</div>
            <div className="flex items-center gap-3">
                <input
                    type="number"
                    min="10"
                    max="48"
                    value={Number(content.brand?.name_size) || ""}
                    placeholder="Auto"
                    onChange={(e) => {
                        const v = e.target.value;
                        update("brand.name_size", v === "" ? 0 : Math.max(10, Math.min(48, parseInt(v, 10) || 0)));
                    }}
                    className="w-32 bg-white border border-oth-line/70 rounded-2xl px-4 py-3 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
                    data-testid="brand-name-size"
                />
                <button
                    type="button"
                    onClick={() => update("brand.name_size", 0)}
                    className="text-xs font-body text-oth-ink3 hover:text-oth-ink transition-colors px-3 py-2"
                    data-testid="brand-name-size-reset"
                >Auto</button>
            </div>
            <div className="text-[11px] text-oth-ink3 mt-1.5 font-body">
                Custom px size for the brand name. Range 10–48 px. Set to <span className="text-oth-ink">Auto</span> to follow the logo-size preset (15 / 17 / 18 px for Small / Medium / Large).
            </div>
        </div>

        <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">Brand name colour</div>
            <div className="flex items-center gap-3">
                <input
                    type="color"
                    value={(content.brand?.name_color || "").trim() || "#0A0A0A"}
                    onChange={(e) => update("brand.name_color", e.target.value)}
                    className="w-12 h-12 rounded-2xl border border-oth-line/70 bg-white cursor-pointer p-1"
                    data-testid="brand-name-color-picker"
                    aria-label="Brand name colour"
                />
                <input
                    type="text"
                    value={content.brand?.name_color || ""}
                    onChange={(e) => update("brand.name_color", e.target.value)}
                    placeholder="#0A0A0A"
                    className="flex-1 bg-white border border-oth-line/70 rounded-2xl px-4 py-3 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
                    data-testid="brand-name-color-text"
                />
                <button
                    type="button"
                    onClick={() => update("brand.name_color", "")}
                    className="text-xs font-body text-oth-ink3 hover:text-oth-ink transition-colors px-3 py-2"
                    data-testid="brand-name-color-reset"
                >Reset</button>
            </div>
            <div className="text-[11px] text-oth-ink3 mt-1.5 font-body">
                Paste a hex value or use the picker. Leave empty for the default (black on the navbar, white on the footer).
            </div>
        </div>

        <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">Logo size</div>
            <div className="flex gap-2" data-testid="brand-logo-size-group">
                {[
                    { v: "sm", label: "Small · 32 px" },
                    { v: "md", label: "Medium · 48 px" },
                    { v: "lg", label: "Large · 64 px" },
                ].map((opt) => {
                    const active = (content.brand?.logo_size || "md") === opt.v;
                    return (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => update("brand.logo_size", opt.v)}
                            className={`flex-1 px-4 py-3 rounded-2xl border text-sm font-body transition-all duration-500 ${
                                active
                                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                                    : "bg-white border-oth-line/70 text-oth-ink2 hover:border-oth-ink"
                            }`}
                            data-testid={`brand-logo-size-${opt.v}`}
                        >{opt.label}</button>
                    );
                })}
            </div>
            <div className="text-[11px] text-oth-ink3 mt-1.5 font-body">
                We recommend <span className="text-oth-ink">Medium</span> — it makes the mark lead while keeping the navbar calm.
            </div>
        </div>

        <ImageUploader
            label="Logo (optional — replaces the blue dot)"
            value={content.brand?.logo_url}
            onChange={(v) => update("brand.logo_url", v)}
            recommended={REC.logo}
            testId="brand-logo"
            aspect="aspect-square"
        />
        <Field label="Calendly URL" value={content.brand?.calendly_url} onChange={(v) => update("brand.calendly_url", v)} hint="Used by every CTA button." testId="brand-calendly" />
        <Field label="Login label" value={content.nav?.login_label} onChange={(v) => update("nav.login_label", v)} testId="nav-login-label" />
        <Field label="CTA label" value={content.nav?.cta_label} onChange={(v) => update("nav.cta_label", v)} testId="nav-cta-label" />
        <ArrayEditor
            items={content.nav?.links}
            onChange={(v) => update("nav.links", v)}
            label="Nav links"
            addNew={() => ({ label: "New", href: "#" })}
            addLabel="Add link"
            testId="nav-links"
            render={(l, set) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Label" value={l.label} onChange={(v) => set({ ...l, label: v })} testId={`nav-link-label-${l.label}`} />
                    <Field label="Href" value={l.href} onChange={(v) => set({ ...l, href: v })} testId={`nav-link-href-${l.label}`} />
                </div>
            )}
        />
    </div>
);

export const SeoTab = ({ content, update }) => (
    <div>
        <Field label="Page title" value={content.seo?.title} onChange={(v) => update("seo.title", v)} hint="Appears in the browser tab and as the headline when the link is shared." testId="seo-title" />
        <Field label="Meta description" type="textarea" value={content.seo?.description} onChange={(v) => update("seo.description", v)} hint="Shown under the link in search results & social previews. Aim for ~155 characters." testId="seo-description" />
        <Field label="Keywords" value={content.seo?.keywords} onChange={(v) => update("seo.keywords", v)} hint="Comma-separated." testId="seo-keywords" />
        <Field label="Canonical site URL" value={content.seo?.site_url} onChange={(v) => update("seo.site_url", v)} hint="e.g. https://onetreehillprime.com" testId="seo-site-url" />
        <ImageUploader
            label="Open Graph / Social share image"
            value={content.seo?.og_image}
            onChange={(v) => update("seo.og_image", v)}
            recommended={REC.og}
            testId="seo-og-image"
            aspect="aspect-[1200/630]"
        />
        <ImageUploader
            label="Favicon"
            value={content.seo?.favicon}
            onChange={(v) => update("seo.favicon", v)}
            recommended={REC.favicon}
            testId="seo-favicon"
            aspect="aspect-square"
        />
    </div>
);

export const HeroTab = ({ content, update }) => (
    <div>
        <Field label="Pill" value={content.hero?.pill} onChange={(v) => update("hero.pill", v)} testId="hero-pill-input" />
        <ArrayEditor
            items={content.hero?.headline_lines}
            onChange={(v) => update("hero.headline_lines", v)}
            label="Headline lines (each renders on its own line)"
            addNew={() => ({ text: "New line", color: "" })}
            addLabel="Add line"
            testId="hero-lines"
            render={(l, set, i) => {
                const line = typeof l === "string" ? { text: l, color: "" } : (l || { text: "", color: "" });
                return (
                    <div className="space-y-3">
                        <Field
                            label={`Line ${i + 1}`}
                            value={line.text}
                            onChange={(v) => set({ ...line, text: v })}
                            testId={`hero-line-text-${i}`}
                        />
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">Line colour (optional)</div>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={(line.color || "").trim() || "#0A0A0A"}
                                    onChange={(e) => set({ ...line, color: e.target.value })}
                                    className="w-10 h-10 rounded-xl border border-oth-line/70 bg-white cursor-pointer p-1"
                                    data-testid={`hero-line-color-picker-${i}`}
                                    aria-label={`Line ${i + 1} colour`}
                                />
                                <input
                                    type="text"
                                    value={line.color || ""}
                                    onChange={(e) => set({ ...line, color: e.target.value })}
                                    placeholder="Inherits from headline default"
                                    className="flex-1 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
                                    data-testid={`hero-line-color-text-${i}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => set({ ...line, color: "" })}
                                    className="text-xs font-body text-oth-ink3 hover:text-oth-ink transition-colors px-3 py-2"
                                    data-testid={`hero-line-color-reset-${i}`}
                                >Reset</button>
                            </div>
                        </div>
                    </div>
                );
            }}
        />
        <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">Headline default colour</div>
            <div className="flex items-center gap-3">
                <input
                    type="color"
                    value={(content.hero?.headline_color || "").trim() || "#0A0A0A"}
                    onChange={(e) => update("hero.headline_color", e.target.value)}
                    className="w-12 h-12 rounded-2xl border border-oth-line/70 bg-white cursor-pointer p-1"
                    data-testid="hero-headline-color-picker"
                    aria-label="Headline default colour"
                />
                <input
                    type="text"
                    value={content.hero?.headline_color || ""}
                    onChange={(e) => update("hero.headline_color", e.target.value)}
                    placeholder="#0A0A0A"
                    className="flex-1 bg-white border border-oth-line/70 rounded-2xl px-4 py-3 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
                    data-testid="hero-headline-color-text"
                />
                <button
                    type="button"
                    onClick={() => update("hero.headline_color", "")}
                    className="text-xs font-body text-oth-ink3 hover:text-oth-ink transition-colors px-3 py-2"
                    data-testid="hero-headline-color-reset"
                >Reset</button>
            </div>
            <div className="text-[11px] text-oth-ink3 mt-1.5 font-body">
                Applied to any line whose individual colour is left empty. Leave both empty for the system default near-black.
            </div>
        </div>
        <Field label="Paragraph" type="textarea" value={content.hero?.paragraph} onChange={(v) => update("hero.paragraph", v)} testId="hero-paragraph-input" />
        <Field label="CTA label" value={content.hero?.cta_label} onChange={(v) => update("hero.cta_label", v)} testId="hero-cta-label" />
        <Field
            label="Stat counter duration (seconds)"
            value={String(content.hero?.counter_seconds ?? 1.6)}
            onChange={(v) => {
                const n = parseFloat(v);
                update("hero.counter_seconds", isNaN(n) ? 1.6 : Math.max(0.5, Math.min(8, n)));
            }}
            hint="How long each hero stat counts up from zero. Range 0.5–8 s."
            testId="hero-counter-seconds"
        />
        <ImageUploader
            label="Hero image"
            value={content.hero?.image_url}
            onChange={(v) => update("hero.image_url", v)}
            recommended={REC.hero}
            testId="hero-image"
        />
        <ArrayEditor
            items={content.hero?.stats}
            onChange={(v) => update("hero.stats", v)}
            label="Stat cards"
            addNew={() => ({ value: "", label: "" })}
            addLabel="Add stat"
            testId="hero-stats"
            render={(s, set) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Value" value={s.value} onChange={(v) => set({ ...s, value: v })} testId={`stat-value-${s.value}`} />
                    <Field label="Label" value={s.label} onChange={(v) => set({ ...s, label: v })} testId={`stat-label-${s.label}`} />
                </div>
            )}
        />
    </div>
);

export const TimesChangingTab = ({ content, update }) => (
    <div>
        <Field label="Eyebrow" value={content.times_changing?.eyebrow} onChange={(v) => update("times_changing.eyebrow", v)} testId="tc-eyebrow" />
        <Field label="Headline" value={content.times_changing?.headline} onChange={(v) => update("times_changing.headline", v)} testId="tc-headline" />
        <Field label="Paragraph" type="textarea" value={content.times_changing?.paragraph} onChange={(v) => update("times_changing.paragraph", v)} testId="tc-paragraph" />
        <ArrayEditor
            items={content.times_changing?.bullets}
            onChange={(v) => update("times_changing.bullets", v)}
            label="Bullets" addNew={() => "New bullet"} addLabel="Add bullet" testId="tc-bullets"
            render={(b, set) => <Field label="Bullet" value={b} onChange={set} testId={`tc-bullet-${b}`} />}
        />
        <ImageUploader
            label="Family image"
            value={content.times_changing?.family_image}
            onChange={(v) => update("times_changing.family_image", v)}
            recommended={REC.family}
            testId="tc-family-image"
            aspect="aspect-[5/4]"
        />
        <Field label="Dashboard title" value={content.times_changing?.dashboard_title} onChange={(v) => update("times_changing.dashboard_title", v)} testId="tc-dash-title" />
        <Field label="Dashboard metric" value={content.times_changing?.dashboard_metric} onChange={(v) => update("times_changing.dashboard_metric", v)} testId="tc-dash-metric" />
        <Field label="Dashboard caption" value={content.times_changing?.dashboard_caption} onChange={(v) => update("times_changing.dashboard_caption", v)} testId="tc-dash-caption" />
        <Field label="Alert title" value={content.times_changing?.alert_title} onChange={(v) => update("times_changing.alert_title", v)} testId="tc-alert-title" />
        <Field label="Alert message" value={content.times_changing?.alert_message} onChange={(v) => update("times_changing.alert_message", v)} testId="tc-alert-msg" />
    </div>
);

export const BenefitsTab = ({ content, update }) => (
    <div>
        <Field label="Eyebrow" value={content.benefits?.eyebrow} onChange={(v) => update("benefits.eyebrow", v)} testId="ben-eyebrow" />
        <Field label="Headline" type="textarea" value={content.benefits?.headline} onChange={(v) => update("benefits.headline", v)} testId="ben-headline" />
        <div className="mb-6 text-[12px] text-oth-ink3 font-body leading-relaxed bg-white/60 border border-oth-line/70 rounded-2xl p-4">
            <span className="font-medium text-oth-ink">Layout note:</span> Up to 5 cards render as a horizontal expandable accordion. The first card is active by default; hover (desktop) or tap (mobile) to switch.
        </div>
        <ArrayEditor
            items={content.benefits?.cards}
            onChange={(v) => update("benefits.cards", v.slice(0, 5))}
            label="Benefit cards (max 5)"
            addNew={() => ({ id: "card-" + Date.now(), title: "", body: "", image: "" })}
            addLabel="Add card" testId="ben-cards"
            render={(c, set) => (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Title" value={c.title} onChange={(v) => set({ ...c, title: v })} testId={`ben-card-title-${c.id}`} />
                        <Field label="Identifier (no spaces)" value={c.id} onChange={(v) => set({ ...c, id: v })} hint="Used internally; leave as-is unless you know why you're changing it." testId={`ben-card-id-${c.id}`} />
                    </div>
                    <Field label="Body" type="textarea" value={c.body} onChange={(v) => set({ ...c, body: v })} testId={`ben-card-body-${c.id}`} />
                    <ImageUploader
                        label="Card background image"
                        value={c.image}
                        onChange={(v) => set({ ...c, image: v })}
                        recommended={REC.benefit}
                        testId={`ben-card-image-${c.id}`}
                        aspect="aspect-[4/5]"
                    />
                </div>
            )}
        />
    </div>
);

export const OurEdgeTab = ({ content, update }) => (
    <div>
        <Field label="Eyebrow" value={content.our_edge?.eyebrow} onChange={(v) => update("our_edge.eyebrow", v)} testId="edge-eyebrow" />
        <Field label="Headline" value={content.our_edge?.headline} onChange={(v) => update("our_edge.headline", v)} testId="edge-headline" />
        <ArrayEditor
            items={content.our_edge?.items}
            onChange={(v) => update("our_edge.items", v)}
            label="Accordion items"
            addNew={() => ({ id: "edge-" + Date.now(), title: "", body: "", image: "" })}
            addLabel="Add item" testId="edge-items"
            render={(it, set) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Title" value={it.title} onChange={(v) => set({ ...it, title: v })} testId={`edge-item-title-${it.id}`} />
                    <div />
                    <div className="col-span-2"><Field label="Body" type="textarea" value={it.body} onChange={(v) => set({ ...it, body: v })} testId={`edge-item-body-${it.id}`} /></div>
                    <div className="col-span-2">
                        <ImageUploader
                            label="Visual panel image"
                            value={it.image}
                            onChange={(v) => set({ ...it, image: v })}
                            recommended={REC.edge}
                            testId={`edge-item-image-${it.id}`}
                            aspect="aspect-[4/3]"
                        />
                    </div>
                </div>
            )}
        />
    </div>
);

export const PhilosophyTab = ({ content, update }) => (
    <div>
        <Field label="Eyebrow" value={content.philosophy?.eyebrow} onChange={(v) => update("philosophy.eyebrow", v)} testId="phi-eyebrow" />
        <ArrayEditor
            items={content.philosophy?.quote_lines}
            onChange={(v) => update("philosophy.quote_lines", v)}
            label="Quote lines" addNew={() => "New line"} addLabel="Add line" testId="phi-lines"
            render={(l, set) => <Field label="Line" value={l} onChange={set} testId={`phi-line-${l}`} />}
        />
        <Field label="Paragraph" type="textarea" value={content.philosophy?.paragraph} onChange={(v) => update("philosophy.paragraph", v)} testId="phi-paragraph" />
        <ArrayEditor
            items={content.philosophy?.highlight_phrases}
            onChange={(v) => update("philosophy.highlight_phrases", v)}
            label="Highlighted phrases" addNew={() => "phrase"} addLabel="Add phrase" testId="phi-highlights"
            render={(h, set) => <Field label="Phrase" value={h} onChange={set} testId={`phi-hl-${h}`} />}
        />
        <ArrayEditor
            items={content.philosophy?.images}
            onChange={(v) => update("philosophy.images", v)}
            label="Image stack (4 images)" addNew={() => ""} addLabel="Add image" testId="phi-images"
            render={(u, set, i) => (
                <ImageUploader
                    label={`Image ${i + 1}`}
                    value={u}
                    onChange={set}
                    recommended={REC.philosophy}
                    testId={`phi-img-${i}`}
                />
            )}
        />
    </div>
);

export const AboutTab = ({ content, update }) => (
    <div>
        <Field label="Eyebrow" value={content.about?.eyebrow} onChange={(v) => update("about.eyebrow", v)} testId="about-eyebrow" />
        <Field label="Headline" value={content.about?.headline} onChange={(v) => update("about.headline", v)} testId="about-headline" />
        <ArrayEditor
            items={content.about?.founders}
            onChange={(v) => update("about.founders", v)}
            label="Founders" addNew={() => ({ name: "", role: "", image: "", story: "" })} addLabel="Add founder" testId="about-founders"
            render={(f, set) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Name" value={f.name} onChange={(v) => set({ ...f, name: v })} testId={`founder-name-${f.name}`} />
                    <Field label="Role" value={f.role} onChange={(v) => set({ ...f, role: v })} testId={`founder-role-${f.name}`} />
                    <div className="col-span-2">
                        <ImageUploader
                            label="Portrait"
                            value={f.image}
                            onChange={(v) => set({ ...f, image: v })}
                            recommended={REC.founder}
                            testId={`founder-image-${f.name}`}
                        />
                    </div>
                    <div className="col-span-2"><Field label="Story" type="textarea" value={f.story} onChange={(v) => set({ ...f, story: v })} hint="Press Enter for a line break. Leave a blank line between paragraphs for spacing." testId={`founder-story-${f.name}`} /></div>
                </div>
            )}
        />
    </div>
);

export const TestimonialsTab = ({ content, update }) => (
    <div>
        <Field label="Eyebrow" value={content.testimonials?.eyebrow} onChange={(v) => update("testimonials.eyebrow", v)} testId="t-eyebrow" />
        <Field
            label="Rotation interval (seconds)"
            value={String(content.testimonials?.rotation_seconds ?? 6)}
            onChange={(v) => {
                const n = parseFloat(v);
                update("testimonials.rotation_seconds", isNaN(n) ? 6 : Math.max(2, Math.min(60, n)));
            }}
            hint="How long each testimonial stays before the strip scrolls up. Min 2s, max 60s."
            testId="t-rotation"
        />
        <ArrayEditor
            items={content.testimonials?.items}
            onChange={(v) => update("testimonials.items", v)}
            label="Testimonials"
            addNew={() => ({ quote: "", highlight: "", author: "", role: "", portrait: "" })}
            addLabel="Add testimonial" testId="t-items"
            render={(t, set) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="col-span-2"><Field label="Quote" type="textarea" value={t.quote} onChange={(v) => set({ ...t, quote: v })} testId={`t-quote-${t.author}`} /></div>
                    <Field label="Highlight phrase" value={t.highlight} onChange={(v) => set({ ...t, highlight: v })} testId={`t-hl-${t.author}`} />
                    <Field label="Author" value={t.author} onChange={(v) => set({ ...t, author: v })} testId={`t-author-${t.author}`} />
                    <Field label="Role" value={t.role} onChange={(v) => set({ ...t, role: v })} testId={`t-role-${t.author}`} />
                    <div className="col-span-2">
                        <ImageUploader
                            label="Portrait"
                            value={t.portrait}
                            onChange={(v) => set({ ...t, portrait: v })}
                            recommended={REC.testimonial}
                            testId={`t-portrait-${t.author}`}
                        />
                    </div>
                </div>
            )}
        />
    </div>
);

export const FaqTab = ({ content, update }) => (
    <div>
        <Field label="Eyebrow" value={content.faq?.eyebrow} onChange={(v) => update("faq.eyebrow", v)} testId="faq-eyebrow" />
        <ArrayEditor
            items={content.faq?.items}
            onChange={(v) => update("faq.items", v)}
            label="FAQ items" addNew={() => ({ q: "", a: "" })} addLabel="Add item" testId="faq-items"
            render={(it, set) => (
                <div className="space-y-3">
                    <Field label="Question" value={it.q} onChange={(v) => set({ ...it, q: v })} testId={`faq-q-${it.q}`} />
                    <Field label="Answer" type="textarea" value={it.a} onChange={(v) => set({ ...it, a: v })} testId={`faq-a-${it.q}`} />
                </div>
            )}
        />
    </div>
);

export const CtaTab = ({ content, update }) => (
    <div>
        <Field label="Headline" type="textarea" value={content.cta?.headline} onChange={(v) => update("cta.headline", v)} testId="cta-headline-input" />
        <Field label="Subtext" value={content.cta?.subtext} onChange={(v) => update("cta.subtext", v)} testId="cta-subtext-input" />
        <Field label="CTA label" value={content.cta?.cta_label} onChange={(v) => update("cta.cta_label", v)} testId="cta-label-input" />
    </div>
);

export const FooterTab = ({ content, update }) => (
    <div>
        <Field label="Tagline" type="textarea" value={content.footer?.tagline} onChange={(v) => update("footer.tagline", v)} testId="footer-tagline" />
        <Field label="Rights" value={content.footer?.rights} onChange={(v) => update("footer.rights", v)} testId="footer-rights" />
        <ArrayEditor
            items={content.footer?.socials}
            onChange={(v) => update("footer.socials", v)}
            label="Socials" addNew={() => ({ label: "", href: "" })} addLabel="Add social" testId="footer-socials"
            render={(s, set) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Label" value={s.label} onChange={(v) => set({ ...s, label: v })} testId={`social-label-${s.label}`} />
                    <Field label="Href" value={s.href} onChange={(v) => set({ ...s, href: v })} testId={`social-href-${s.label}`} />
                </div>
            )}
        />
        <ArrayEditor
            items={content.footer?.columns}
            onChange={(v) => update("footer.columns", v)}
            label="Link columns" addNew={() => ({ title: "", links: [] })} addLabel="Add column" testId="footer-columns"
            render={(col, set) => (
                <div>
                    <Field label="Title" value={col.title} onChange={(v) => set({ ...col, title: v })} testId={`footer-col-${col.title}`} />
                    <ArrayEditor
                        items={col.links}
                        onChange={(v) => set({ ...col, links: v })}
                        label="Links" addNew={() => ({ label: "", href: "" })} addLabel="Add link" testId={`footer-col-links-${col.title}`}
                        render={(l, setL) => (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Label" value={l.label} onChange={(v) => setL({ ...l, label: v })} testId={`footer-link-label-${l.label}`} />
                                <Field label="Href" value={l.href} onChange={(v) => setL({ ...l, href: v })} testId={`footer-link-href-${l.label}`} />
                            </div>
                        )}
                    />
                </div>
            )}
        />

        <div className="mt-10 text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Disclaimer modal</div>
        <div className="mb-4 p-4 rounded-2xl bg-[#0A0A0A] text-white/85 font-body text-[13px] leading-relaxed" data-testid="disclaimer-instructions">
            Shown when a visitor clicks the <span className="text-white">"Disclaimer"</span> link in the footer (Resources column). To remove it from the footer, delete the link with href <span className="text-white">#disclaimer</span> from the Resources column above.
        </div>
        <Field label="Modal eyebrow (small label above title)" value={content.footer?.disclaimer?.eyebrow} onChange={(v) => update("footer.disclaimer.eyebrow", v)} testId="disclaimer-eyebrow" />
        <Field label="Modal title" value={content.footer?.disclaimer?.title} onChange={(v) => update("footer.disclaimer.title", v)} testId="disclaimer-title" />
        <Field
            label="Disclaimer text"
            type="textarea"
            value={content.footer?.disclaimer?.body}
            onChange={(v) => update("footer.disclaimer.body", v)}
            hint="Press Enter for a line break. Leave a blank line between paragraphs for spacing. Leading spaces are preserved."
            testId="disclaimer-body"
        />
    </div>
);
